/**
 * Leaf schema helpers for the BFF seam (`bff-schemas.ts`) — branded/enum
 * wire-value primitives, split out (W3.2, auth-debt epic) so the seam file
 * stays under the max-lines ceiling as the contract grows. Pure zod; no
 * other dependencies, so nothing here can cycle back into the seam.
 */
import * as z from 'zod/mini';

/**
 * A branded wire string (`Brand<string, …>`): runtime = "is a string". The
 * brand is a compile-time-only refinement of `string`, so `z.custom<B>` with a
 * string check claims nothing the runtime doesn't verify — it is the
 * schema-side (cast-free) mirror of the sanctioned branded-constructor
 * crossings in `@letsgethome/types/branded`.
 */
export function brandedString<B extends string>(): z.ZodMiniType<B> {
  return z.custom<B>((value) => typeof value === 'string');
}

/** A branded wire number (`Brand<number, …>`): runtime = "is a number". */
export function brandedNumber<B extends number>(): z.ZodMiniType<B> {
  return z.custom<B>((value) => typeof value === 'number');
}

/**
 * A branded bigint (`Brand<bigint, …>`) carried over JSON: JSON cannot encode a
 * bigint, so the wire value is an integral number/string — coerce it back to
 * the `bigint` the contract type promises (`z.coerce.bigint()` rejects
 * non-integral / junk input; the piped custom check re-states the branded
 * output type cast-free). This makes the post-parse VALUE honestly match the
 * contract type instead of a number masquerading as `CentsBigInt` behind the
 * old cast.
 */
export function brandedCoercedBigInt<B extends bigint>(): z.ZodMiniType<B> {
  return z.pipe(
    z.coerce.bigint(),
    z.custom<B>((value) => typeof value === 'bigint')
  );
}

/**
 * The NAME union of a generated proto TS enum (`keyof typeof Enum` — the
 * view-model enum pattern, e.g. `'ADDRESS' | 'SIZE' | …`). Derived from the
 * enum OBJECT at runtime (numeric reverse-mapping keys filtered out), so the
 * accepted vocabulary and the proto enum can't drift.
 */
export function protoEnumNameSchema<E extends Record<string, string | number>>(
  enumObject: E
): z.ZodMiniType<keyof E & string> {
  const names = Object.keys(enumObject).filter((key) => Number.isNaN(Number(key)));
  return z.enum(names) as z.ZodMiniType<keyof E & string>;
}

/**
 * The VALUE union of a generated proto TS enum (numeric wire tags, e.g.
 * `UserProfile.roles`): accepts exactly the enum's numeric values, DERIVED from
 * the enum object at runtime (reverse-mapping name strings filtered out). Typed
 * via the explicit `V` (the declared enum type) rather than `z.enum(Enum)`'s
 * member-literal union, which TS does not consider identical to `V`.
 */
export function protoEnumValueSchema<V extends number>(
  enumObject: Record<string, string | number>
): z.ZodMiniType<V> {
  const values = new Set(Object.values(enumObject).filter((value) => typeof value === 'number'));
  return z.custom<V>((value) => typeof value === 'number' && values.has(value));
}
